<script lang="ts">
    import { setContext } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import { DateTime } from 'luxon'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { spath } from 'hydria/svelte_helpers'
    import { urltitle } from 'hydria/utils/strings'

    import { Breadcrumb } from 'breadcrumb'

    import type { ILocalDocument, ILocalRevision } from './pageDefs'

    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import GenDateTimeInput from 'hydria/components/Generics/GenDateTimeInput.svelte'
    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import GenUrlTitleInput from 'hydria/components/Generics/GenUrlTitleInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'
    import ImageSelection from '../BaseFields/ImageSelection.svelte'
    import MetaDesc from '../BaseFields/MetaDesc.svelte'
    import ShowInModalFields from './ShowInModalFields.svelte'
    import TagsField from './TagsField.svelte'

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    const revFields = ['title', 'url_title', 'meta_desc', 'content_html', 'content_home_html', 'vignette_id', 'modal_signout', 'deleg', 'order_signout', 'tags', 'lg', 'visual_date']
    const revSchema = yup.object().shape({
        title: yup.string().required(),
        url_title: yup
            .string()
            .required()
            .test('url_titleIsValid', '${path} is not valid', (value: string) => urltitle(value) === value),
        meta_desc: yup.string().optional().nullable(),
        content_html: yup.string().required(),
        content_home_html: yup.string().optional().nullable(),
        vignette_id: yup.string().required(),
        modal_signout: yup.boolean(),
        deleg: yup.boolean().optional().nullable(),
        order_signout: yup.string().optional(),
        visual_date: yup
            .date()
            // .transform((value, oValue) => {
            //     console.log('transform: in=', {value}, 'out=',)
            //     return value ? value.toJSDate() : null
            // })
            .required(),
        // .optional()
        // .nullable(),
        tags: yup.array().of(yup.string()).required(),
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
    })

    class LocalDocument extends HydriaDocument {
        docType = 'nouvelle'
        docTypePlural = 'nouvelles'

        docTypeName = tgettext('Nouvelle')
        docTypeNamePlural = tgettext('Nouvelles')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)

    const docSaved = sderived(Document.document, ($doc) => $doc as ILocalDocument)
    const revSaved = sderived(Document.revision, ($rev) => {
        const revV = (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision

        if (!revV) return revV

        // if (revV.visual_date === undefined) revV.visual_date = null
        if (revV.visual_date == null) {
            revV.visual_date = DateTime.now() //.toJSDate()
        }

        // if (revV.visual_date && (revV.visual_date as any).isLuxonDateTime === true) revV.visual_date = (revV.visual_date as any).toJSDate()

        return revV
    })
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)

    setContext('revEdit', revEdit)
    setContext('Document', Document)
    setContext('attachments', Document.attachedFiles)

    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    // $: if ($status == 'ready' && $revEdit && $revEdit.sections == null) $revEdit.sections = []

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural.replace('_', '-')}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    icon: 'mdi:alert',
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields}>
    {#if $revEdit}
        <div class="card">
            <div class="card-header text-bg-primary">
                {$_('Document')}&#x2009;: {$_('Méta données')}
            </div>
            <div class="card-body d-grid gap-2">
                <GenTextInput label={tgettext('Titre')} saved={$revSaved.title} bind:value={$revEdit.title} isValid={(v) => validateSyncAt(revSchema, 'title', { title: v })} helpText={tgettext('Ce texte est utilisé pour le titre de la page, dans la barre du navigateur.')} disabled={$status != 'ready'} />
                <GenUrlTitleInput label={tgettext('Titre URL')} title={$revEdit.title} saved={$revSaved.url_title} bind:value={$revEdit.url_title} isValid={(v) => validateSyncAt(revSchema, 'url_title', { url_title: v })} urlTitle={Document.urlTitle} lg={$revEdit.lg} disabled={$status != 'ready'} />

                <LanguageSelector bind:value={$revEdit.lg} disabled={$status != 'ready'} />

                <MetaDesc saved={$revSaved.meta_desc} bind:value={$revEdit.meta_desc} disabled={$status != 'ready'} isValid={(v) => validateSyncAt(revSchema, 'meta_desc', { meta_desc: v })} />

                <ShowInModalFields {revEdit} {revSaved} disabled={$status != 'ready'} />
                <GenTextInput label={tgettext('Ordre dans modal')} saved={$revSaved.order_signout} bind:value={$revEdit.order_signout} isValid={(v) => validateSyncAt(revSchema, 'order_signout', { order_signout: v })} helpText={tgettext('Permet d’ordonner dans le modal.')} disabled={$status != 'ready' || !$revEdit.modal_signout} hidden={!$revEdit.modal_signout} emptyIsNull={false} />

                <TagsField bind:value={$revEdit.tags} saved={$revSaved.tags} disabled={$status != 'ready'} label={tgettext('Tags')} />

                <GenDateTimeInput
                    label={tgettext('Date affichée')}
                    value={$revEdit.visual_date}
                    saved={$revSaved.visual_date}
                    isValid={(v) => {
                        const x = validateSyncAt(revSchema, 'visual_date', { visual_date: v })
                        return !!x
                    }}
                    withTime={true}
                    allowClear={false}
                    save={(e) => {
                        $revEdit.visual_date = e
                        // console.log('**save:', e && e.isLuxonDateTime === true, e && e.isLuxonDateTime === true ? e.toJSDate() : e)
                        // $revEdit.visual_date = e && e.isLuxonDateTime === true ? e.toJSDate() : e
                    }}
                />

                <ImageSelection
                    disabled={$status != 'ready'}
                    value={$revEdit.vignette_id}
                    saved={$revSaved ? $revSaved.vignette_id : undefined}
                    allowEmpty={false}
                    label={tgettext('Image')}
                    previewName={'vignette'}
                    {Document}
                    save={(e) => {
                        $revEdit.vignette_id = e ? e : ''
                    }}
                />
            </div>
        </div>

        <div class="card">
            <div class="card-header text-bg-primary">{$_('Accueil')}</div>
            <div class="card-body d-grid gap-2">
                <IntroHtml
                    label={tgettext('Article')}
                    helpText={tgettext('Ce texte est affiché sur l’accueil et dans la liste d’articles. S’il est laissé vide, le texte entier sera affiché.')}
                    saved={$revSaved.content_home_html}
                    bind:value={$revEdit.content_home_html}
                    disabled={$status != 'ready'}
                    isValid={(v) => {
                        return true
                        // if (v == null) return true
                        // const clean = v.replaceAll(/<p><\/p>/gi, '')
                        // return clean != ''
                    }}
                />
            </div>
        </div>

        <div class="card">
            <div class="card-header text-bg-primary">{$_('Contenu')}</div>
            <div class="card-body d-grid gap-2">
                <IntroHtml
                    label={tgettext('Article')}
                    helpText={undefined}
                    saved={$revSaved.content_html}
                    bind:value={$revEdit.content_html}
                    disabled={$status != 'ready'}
                    isValid={(v) => {
                        if (v == null) return false
                        const clean = v.replaceAll(/<p><\/p>/gi, '')
                        return clean != ''
                    }}
                />
            </div>
        </div>
    {/if}
</DocumentEdit>
